import { useCallback, useEffect, useState } from "react";
import { KeyRound, Lock, ShieldAlert, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Card } from "@/components/Ui";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { IntegrationCard } from "@/components/IntegrationCard";
import { AutomationsCard } from "@/components/AutomationsCard";

function UnlockCard({ onUnlocked }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/integrations/unlock", { code }); onUnlocked(); } catch (e) { toast.error(errMsg(e)); setCode(""); } finally { setBusy(false); }
  };
  return (
    <Card className="p-8 max-w-md" data-testid="integrations-unlock-card">
      <ShieldAlert className="h-6 w-6 text-sky-600 mb-3" />
      <p className="font-heading font-semibold text-slate-900">Conferma la tua identità</p>
      <p className="text-sm text-slate-500 mt-1 mb-5">Inserisci il codice a 6 cifre dell'app autenticatore. L'accesso resta sbloccato per 10 minuti e viene registrato nel Registro Audit.</p>
      <InputOTP maxLength={6} value={code} onChange={setCode} onComplete={() => {}} data-testid="integrations-unlock-code">
        <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
      </InputOTP>
      <Button className="mt-5 bg-[#0A2540] hover:bg-[#031B33] text-white" disabled={busy || code.length !== 6} onClick={submit} data-testid="integrations-unlock-submit">
        {busy ? "Verifica…" : "Sblocca"}
      </Button>
    </Card>
  );
}

export default function Integrations() {
  const [access, setAccess] = useState(null);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/access");
      setAccess(data);
      if (data.unlocked_until) setItems((await api.get("/integrations")).data);
    } catch (e) {
      if (e.response?.status === 423) setAccess((a) => ({ ...a, unlocked_until: null }));
      else toast.error(errMsg(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!access?.unlocked_until) return;
    const ms = new Date(access.unlocked_until) - Date.now();
    const t = setTimeout(() => { setItems([]); setAccess((a) => ({ ...a, unlocked_until: null })); toast("Area collegamenti bloccata per inattività"); }, Math.max(ms, 0));
    return () => clearTimeout(t);
  }, [access?.unlocked_until]);

  const runReminder = async () => {
    try {
      const { data } = await api.post("/integrations/reminder/run");
      data.sent ? toast.success(`Email inviata: ${data.items.length} credenziali da rinnovare`) : toast.success("Nessuna credenziale oltre gli 11 mesi");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const lockNow = async () => { await api.post("/integrations/lock"); setItems([]); setAccess((a) => ({ ...a, unlocked_until: null })); };

  if (!access) return null;
  return (
    <div data-testid="integrations-page">
      <PageHeader eyebrow="Riservato all'Amministratore titolare" title="Collegamenti tecnici"
        subtitle="Credenziali dei servizi esterni usati dal CRM. Sono cifrate (AES-256-GCM), non vengono mai mostrate dopo il salvataggio e prevalgono sui Secrets di pubblicazione senza bisogno di ripubblicare. Ogni lunedì ricevi un promemoria email per quelle salvate da oltre 11 mesi.">
        {access.unlocked_until && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={runReminder} data-testid="integrations-reminder-btn"><BellRing className="h-4 w-4 mr-1" />Controlla scadenze</Button>
            <Button variant="outline" onClick={lockNow} data-testid="integrations-lock-btn"><Lock className="h-4 w-4 mr-1" />Blocca ora</Button>
          </div>
        )}
      </PageHeader>
      {!access.totp_enabled ? (
        <Card className="p-6 max-w-md text-sm text-amber-800" data-testid="integrations-no-totp"><KeyRound className="h-5 w-5 mb-2" />Configura prima l'app autenticatore (MFA) al prossimo accesso.</Card>
      ) : !access.unlocked_until ? (
        <UnlockCard onUnlocked={load} />
      ) : (
        <div className="grid xl:grid-cols-2 gap-6">
          <AutomationsCard />
          {items.map((it) => <IntegrationCard key={it.provider} item={it} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}
